// The live session, as a module-level holder rather than React context.
//
// Why not context: offline.ts registers persisted mutation defaults whose mutationFn calls
// api.startService(...) directly. Those mutations are rehydrated from IndexedDB and resumed
// OUTSIDE any React render (PersistQueryClientProvider's onSuccess → resumePausedMutations),
// so there is no component tree to read a context from at that moment. A module holder is
// what the old build-time `config.businessId` already was — this keeps api.ts's signatures
// untouched while making the values session-derived.
//
// Nothing here is persisted: the ID token is read fresh from the live Firebase session on
// every call and never written to storage.

export type AuthSession = {
  uid: string;
  getIdToken(force?: boolean): Promise<string>;
  signOut(): Promise<void>;
};

export type Scope = { businessId: string; siteId: string };

let authSession: AuthSession | null = null;
let scope: Scope | null = null;

export function setAuthSession(session: AuthSession | null): void {
  authSession = session;
  if (!session) scope = null; // signing out drops the scope with the identity
}

export function getAuthSession(): AuthSession | null {
  return authSession;
}

export function setScope(next: Scope | null): void {
  scope = next;
}

export function getScope(): Scope | null {
  return scope;
}

// For the tenant-scoped API paths. Throwing is correct rather than defensive: every caller
// runs behind the ScopeGate, so a null scope here is a wiring bug, and a thrown error is far
// easier to diagnose than a request to "/businesses/undefined/...".
export function requireScope(): Scope {
  if (!scope) throw new Error("No business/site scope — the app is not signed in and scoped yet.");
  return scope;
}
